import {Price} from '@aboutyou/grpc-clients/tadarida/aysa_api/models//price.types';

import {PriceV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/price_v2.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {
  BrandTracker,
  ProductTracker,
  PromotionTracker,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {
  TrackingElementContext,
  TrackingProduct,
  TrackingRecoElement,
  TrackingRecoSectionContext,
  TrackingSectionContext,
} from '@aboutyou/legacy-tracking-sdk';
import {SizePrice} from './types';

export const trackingProductGetter = ({
  product,
  price,
  brand,
  streamPosition,
  sortChannel,
  pageType,
  recoType,
  headline,
  elements,
  sectionIndex,
  rowIndexInSection,
  creativeName,
  promotionId,
  blockTypeAndId,
  badges,
}: {
  product: ProductTracker | Product;
  price?: Price | PriceV2;
  brand: BrandTracker;
  streamPosition: number;
  sectionIndex?: number;
  rowIndexInSection?: number;
  sortChannel?: string;
  pageType?: string;
  recoType?: string;
  headline?: string;
  elements?: TrackingRecoElement[];
  creativeName?: PromotionTracker['creativeName'];
  promotionId?: PromotionTracker['promotionId'];
  blockTypeAndId?: string;
  badges: string[];
}) => {
  const section = getSections({
    sortChannel,
    pageType,
    recoType,
    headline,
    elements,
    sectionIndex,
    blockTypeAndId,
  }).section;

  const element = {
    type: 'product-list-element-content',
    rowIndexInSection: rowIndexInSection ?? NaN,
    columnIndexInSection: NaN,
    elementIndexInSection: NaN,
    productIndexOnPage: streamPosition,
  } as TrackingElementContext;

  return {
    product: getProduct({
      product,
      price,
      brand,
      creativeName,
      promotionId,
      badges,
    }),
    section,
    element,
    creativeName,
    promotionId,
  };
};

const isTracker = (
  product: ProductTracker | Product,
): product is ProductTracker => {
  return (product as ProductTracker).productId !== undefined;
};

const getProduct = ({
  product,
  price,
  brand,
  promotionId,
  creativeName,
  badges,
}: {
  product: ProductTracker | Product;
  price?: Price | PriceV2;
  brand: BrandTracker;
  promotionId?: PromotionTracker['promotionId'];
  creativeName?: PromotionTracker['creativeName'];
  badges: string[];
}): TrackingProduct => {
  const {sponsoredType, isSponsored} = product;

  const minPrice = extractMinPrice(
    isTracker(product) ? price : product.price?.minPrice,
  );
  return {
    id: isTracker(product) ? product.productId : product.id,
    name: isTracker(product) ? product.productName : product.name,
    brand: {
      id: brand?.id,
      name: brand?.name,
    },
    price: getPrice(price),
    isSoldOut: !price?.tracker?.price,
    isSponsored,
    sponsoredType,
    url: isTracker(product) ? product.linkTarget : product.path,
    minPrice: getMinPrices(minPrice, !price),
    // those fields are not needed, therefore left empty
    masterKey: '',
    colors: [],
    merchantId: '',
    promotionId,
    creativeName,
    badges,
  } as unknown as TrackingProduct;
};

const extractMinPrice = (
  price?: Price | PriceV2 | SizePrice,
): SizePrice | undefined => {
  if (!price) return undefined;
  if ('tracker' in price) {
    return {
      withTax: price.tracker.price,
      tax: price.tracker.price - price.tracker.priceWithoutTax,
      campaignReduction: price.tracker.campaignReduction
        ? {
            reductionAmountWithTax: price.tracker.campaignReduction,
          }
        : undefined,
      currencyCode: price.tracker.currencyCode,
      saleReduction: price.tracker.saleReduction
        ? {
            reductionAmountWithTax: price.tracker.saleReduction,
          }
        : undefined,
    };
  } else {
    return price;
  }
};

const getMinPrices = (price: SizePrice | undefined, isSoldOut: boolean) => {
  if (price === undefined && isSoldOut) {
    return {
      withTax: 0,
      withoutTax: 0,
      campaignReductionWithTax: 0,
      currency: '',
      saleReductionWithTax: 0,
    };
  } else if (price === undefined) {
    throw new Error('productTile.product.price.minPrice is undefined');
  }

  const {
    currencyCode,
    tax,
    withTax,
    saleReduction,
    campaignReduction,
    couponReduction,
  } = price;

  return {
    withTax,
    withoutTax: withTax - tax,
    campaignReductionWithTax: campaignReduction?.reductionAmountWithTax || 0,
    currency: currencyCode,
    saleReductionWithTax: saleReduction?.reductionAmountWithTax || 0,
    fullPrice:
      withTax +
      (saleReduction?.reductionAmountWithTax || 0) +
      (campaignReduction?.reductionAmountWithTax || 0) +
      (couponReduction?.reductionAmountWithTax || 0),
    couponReductionWithTax: couponReduction?.reductionAmountWithTax || 0,
  };
};

export const getPrice = (price?: Price | PriceV2): Price | PriceV2 => {
  if (price) {
    return price;
  }

  return {
    price: {amount: 0, currencyCode: ''},
    hasDifferentPrices: false,
    reductions: [],
    priceTags: [],
    tracker: {
      currencyCode: '',
      price: 0,
      priceWithoutTax: 0,
      fullPrice: 0,
    },
  };
};

export const getSections = ({
  sortChannel,
  pageType,
  recoType,
  headline,
  elements,
  sectionIndex,
  blockTypeAndId,
  linkedContent,
  streamPosition,
}: {
  sectionIndex?: number;
  sortChannel?: string;
  pageType?: string;
  recoType?: string;
  headline?: string;
  elements?: TrackingRecoElement[];
  blockTypeAndId?: string;
  linkedContent?: string;
  streamPosition?: number;
} = {}) => {
  if (
    pageType !== 'adp' &&
    pageType !== 'wishlist' &&
    pageType !== 'brand_discover' &&
    pageType !== 'highlight_page' &&
    pageType !== 'customPage'
  ) {
    return {
      section: {
        type: 'stream',
        sortChannel: sortChannel ?? '',
        sectionIndexOnPage: 1,
        recoType,
        linkedContent,
      } as TrackingSectionContext,
      element: {
        type: 'element-context',
        rowIndexInSection: 1,
        columnIndexInSection: 1,
        elementIndexInSection: streamPosition || 0,
        productIndexOnPage: 1,
      } as TrackingElementContext,
    };
  } else {
    return {
      section: {
        type: elements !== undefined ? 'reco' : 'simple',
        recoType,
        blockTypeAndId,
        sectionIndexOnPage: sectionIndex ?? 0,
        initiallyVisibleColumns: 1,
        elements: elements || [{id: NaN, type: 'product'}], // fall back
        headline,
        linkedContent,
      } as TrackingRecoSectionContext,
      element: {
        type: 'element-context',
        rowIndexInSection: 1,
        columnIndexInSection: 1,
        elementIndexInSection: streamPosition || 0,
        productIndexOnPage: 1,
      } as TrackingElementContext,
    };
  }
};
